import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Chart, ActiveElement, Point } from 'chart.js'
import { DonutLegendItem } from '../types'

interface ChartVisibilityState {
    [key: string]: boolean
}

export const useChartVisibility = (legendItems: DonutLegendItem[]) => {
    const [visibilityState, setVisibilityState] = useState<ChartVisibilityState>(() => {
        return legendItems.reduce((acc, item) => {
            acc[item.label] = false
            return acc
        }, {} as ChartVisibilityState)
    })

    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
    const [pendingHoverIndex, setPendingHoverIndex] = useState<number | null>(null)
    const [activeHoverIndex, setActiveHoverIndex] = useState<number | null>(null)

    const chartInstancesRef = useRef<Record<number, Chart<'doughnut'>>>({})

    const hoverTimerRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        return () => {
            if (hoverTimerRef.current !== null) {
                clearTimeout(hoverTimerRef.current)
            }
        }
    }, [])

    const hiddenItems = useMemo(() => {
        if (Object.keys(visibilityState).length === 0 && legendItems.length > 0) {
            return legendItems.reduce((acc, item) => {
                acc[item.label] = false
                return acc
            }, {} as ChartVisibilityState)
        }
        return visibilityState
    }, [legendItems, visibilityState])

    const registerChart = useCallback((index: number, chart: Chart<'doughnut'>) => {
        chartInstancesRef.current[index] = chart
    }, [])

    const clearTooltips = useCallback((shouldUpdate: boolean = true) => {
        Object.values(chartInstancesRef.current).forEach((chart) => {
            if (!chart?.canvas?.ownerDocument) return
            const none: ActiveElement[] = []
            chart.setActiveElements(none)
            chart.tooltip.setActiveElements(none, { x: 0, y: 0 })
            if (shouldUpdate) chart.update()
        })
    }, [])

    const clearAllHoverStates = useCallback(() => {
        if (hoverTimerRef.current !== null) {
            clearTimeout(hoverTimerRef.current)
            hoverTimerRef.current = null
        }

        setHoveredIndex(null)
        setPendingHoverIndex(null)
        setActiveHoverIndex(null)

        clearTooltips(true)
    }, [clearTooltips])

    const showAllItems = useCallback(() => {
        const resetState = legendItems.reduce((acc, item) => {
            acc[item.label] = false
            return acc
        }, {} as ChartVisibilityState)

        setVisibilityState(resetState)
        clearAllHoverStates()
    }, [legendItems, clearAllHoverStates])

    const toggleItem = useCallback(
        (index: number) => {
            const label = legendItems[index]?.label
            if (!label) return

            const visibleCount = legendItems.length - Object.values(visibilityState).filter(Boolean).length

            if (visibleCount === 1 && !visibilityState[label]) {
                showAllItems()
                return
            }

            setVisibilityState((prev) => ({
                ...prev,
                [label]: !prev[label],
            }))

            clearAllHoverStates()
        },
        [legendItems, visibilityState, showAllItems, clearAllHoverStates]
    )

    const focusItem = useCallback(
        (index: number) => {
            const label = legendItems[index]?.label
            if (!label) return

            const visibleCount = legendItems.length - Object.values(visibilityState).filter(Boolean).length
            const isAlreadyFocused = visibleCount === 1 && !visibilityState[label]

            if (isAlreadyFocused) {
                showAllItems()
                return
            }

            const focusState = legendItems.reduce((acc, item) => {
                acc[item.label] = item.label !== label
                return acc
            }, {} as ChartVisibilityState)

            setVisibilityState(focusState)
            clearAllHoverStates()
        },
        [legendItems, visibilityState, showAllItems, clearAllHoverStates]
    )

    const performHover = useCallback(
        (idx: number) => {
            const itemLabel = legendItems[idx]?.label
            if (!itemLabel || hiddenItems[itemLabel]) {
                clearAllHoverStates()
                return
            }

            setPendingHoverIndex(null)
            setActiveHoverIndex(idx)

            Object.values(chartInstancesRef.current).forEach((chart) => {
                if (!chart?.canvas?.ownerDocument) return
                const meta = chart.getDatasetMeta(0)
                if (!meta?.data[idx]) return

                const el = meta.data[idx]
                const { x, y } = el.getProps(['x', 'y'], true) as { x: number; y: number }
                const pos: Point = { x, y }
                const active: ActiveElement[] = [{ datasetIndex: 0, index: idx, element: el }]

                chart.setActiveElements(active)
                chart.tooltip.setActiveElements(active, pos)
                chart.update('none')
            })
        },
        [legendItems, hiddenItems, clearAllHoverStates]
    )

    const clearHoverState = useCallback(() => {
        setHoveredIndex(null)
        setPendingHoverIndex(null)
        setActiveHoverIndex(null)
        clearTooltips(true)
    }, [clearTooltips])

    const setHover = useCallback(
        (index: number | null) => {
            if (hoverTimerRef.current !== null) {
                clearTimeout(hoverTimerRef.current)
                hoverTimerRef.current = null
            }

            if (index === null) {
                clearHoverState()
                return
            }

            const itemLabel = legendItems[index]?.label
            if (!itemLabel || hiddenItems[itemLabel]) {
                clearHoverState()
                return
            }

            setHoveredIndex(index)
            setPendingHoverIndex(index)

            hoverTimerRef.current = setTimeout(() => {
                performHover(index)
                hoverTimerRef.current = null
            }, 1000)
        },
        [performHover, clearHoverState, legendItems, hiddenItems]
    )

    return {
        hiddenItems,
        hoveredIndex,
        pendingHoverIndex,
        activeHoverIndex,
        registerChart,
        toggleItem,
        focusItem,
        setHover,
    }
}
